import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useQuery } from '@apollo/client';
import FETCH_LAST_DISTRIBUTION_BY_ICN from 'src/graphql/querys/sira/fetchLastDistributionInstance';
import GET_DISTRIBUTION_CLASS from 'src/graphql/querys/sira/fetchDistributionClassByICN';
import { GetDistributionClassQuery } from 'src/__generated__/graphql';
import { useDispatch, useSelector } from 'src/store';

// GET_DISTRIBUTION_CLASS queda como DocumentNode plano (no typed-gql) para no cambiar el tipo
// inferido en sus otros consumidores (distribution/topology); acá anotamos el shape a mano.
type DistributionCanalClass =
  GetDistributionClassQuery['siraDistributionClass']['canals'][number];
import { setDistributionStationIds, setShowDistributionOptions } from 'src/slices/map';
import { useLocation } from 'react-router';
import { Trans } from '@lingui/macro';
import TimeElapsed from './TimeElapsed';
import { useFormatters } from '@/hooks/useFormatters';

const LastDistributionData = () => {
  const { formatNumber } = useFormatters();
  const location = useLocation();
  const dispatch = useDispatch();
  // TODO: selectedIrrigationIds fue renombrado a selectedCollectionIds en el slice (PR AMARU-157)
  // pero collection IDs !== irrigation IDs. Las queries SIRA necesitan irrigationChannelNetworkId.
  // El campo ya no existe en el store → hoy este componente está inactivo (queries siempre skip,
  // render null). Se preserva ese comportamiento a propósito; revivirlo requiere decidir la fuente
  // del id (opciones abajo) — ver ticket follow-up (AMARU-571 § LastDistributionData).
  // Opciones: (A) restaurar selectedIrrigationIds derivado de device.profile.irrigation.id al cargar devices,
  // (B) derivar desde map_store.stations, (C) exponer irrigationId en el fragmento GQL de icnCollections.
  const selectedIrrigationIds: number[] = [];
  const [selectedIrrigationId] = selectedIrrigationIds;
  const { stations } = useSelector((state) => state.map_store);
  const [canalClassToSum, setCanalClassToSum] = useState<DistributionCanalClass[] | null>(null);
  const [actualDistributionFlow, setActualDistributionFlow] = useState<number | null>(null);
  const { data: distributionData, loading: distributionLoading } = useQuery(
    FETCH_LAST_DISTRIBUTION_BY_ICN,
    {
      skip: selectedIrrigationId == null,
      variables: {
        irrigationChannelNetworkId: selectedIrrigationId,
      },
    },
  );
  useQuery(GET_DISTRIBUTION_CLASS, {
    skip: selectedIrrigationId == null,
    variables: {
      irrigationChannelNetworkId: selectedIrrigationId,
    },
    onCompleted: (data) => {
      if (data) {
        const { canals } = data.siraDistributionClass;
        setCanalClassToSum(
          canals.filter((canal: DistributionCanalClass) => canal.distributionSum),
        );
      }
    },
  });
  const distribution = distributionData?.latestSiraDistributionInstance;

  useEffect(() => {
    if (stations && distribution) {
      dispatch(setShowDistributionOptions(true));
      dispatch(
        setDistributionStationIds(distribution.canals.map((canal) => canal.inletDevice.id)),
      );
    } else {
      dispatch(setShowDistributionOptions(false));
    }
  }, [stations, distribution]);

  useEffect(() => {
    if (canalClassToSum && stations) {
      const distributionStationIds = canalClassToSum.map((canal) => canal.inletDevice.id);
      setActualDistributionFlow(
        stations.reduce((acc, station) => {
          const { id, flow } = station;
          if (distributionStationIds.includes(id)) {
            return acc + flow;
          }
          return acc;
        }, 0),
      );
    }
  }, [canalClassToSum, stations]);

  if (location.pathname !== '/' || !distribution) return null;

  return (
    <Box
      display="flex"
      flexDirection={['column', 'row']}
      flexWrap="wrap"
      justifyContent="space-around"
    >
      <Box display="flex" flexDirection="row" mr={2}>
        <Typography
          sx={{
            mr: 1,
            fontSize: '0.8rem',
          }}
        >
          <Trans>Total Reparto</Trans>
        </Typography>
        {distributionLoading ? (
          <Typography fontSize="0.8rem">
            <Trans>Cargando...</Trans>
          </Typography>
        ) : (
          <Typography fontWeight="bold" fontSize="0.8rem">
            {distribution && distribution.distributionWaterFlow != null
              ? formatNumber(distribution.distributionWaterFlow) + ' L/s'
              : '-'}
          </Typography>
        )}
      </Box>
      <Box display="flex" flexDirection="row" mr={2}>
        <Typography
          sx={{
            mr: 1,
            fontSize: '0.8rem',
          }}
        >
          <Trans>Flujo Total Actual</Trans>
        </Typography>
        {distributionLoading ? (
          <Typography fontSize="0.8rem">
            <Trans>Cargando...</Trans>
          </Typography>
        ) : (
          <Typography fontWeight="bold" fontSize="0.8rem">
            {actualDistributionFlow !== null ? formatNumber(actualDistributionFlow) + ' L/s' : '-'}
          </Typography>
        )}
      </Box>
      <Box display="flex" flexDirection="row">
        <Typography
          sx={{
            mr: 1,
            fontSize: '0.8rem',
          }}
        >
          <Trans>Último reparto</Trans>
        </Typography>
        {distributionLoading ? (
          <Typography fontSize="0.8rem">
            <Trans>Cargando...</Trans>
          </Typography>
        ) : (
          <Typography fontWeight="bold" fontSize="0.8rem">
            {distribution && distribution.distributionStartDate ? (
              <TimeElapsed
                timestamp={new Date(distribution.distributionStartDate).getTime()}
              />
            ) : (
              '-'
            )}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default LastDistributionData;
